<template>
  <div class="condition-row">
    <!-- The field: named, with the path it stands for underneath. The path is
         what the site evaluates, so whoever knows it can still recognise it —
         but nobody has to read it to set a rule up. -->
    <v-select
      class="cond-field"
      append-to-body
      :calculate-position="dropdownBelowField"
      v-model="variable"
      :options="fieldOptions"
      label="label"
      :reduce="(v) => v.value"
      :clearable="false"
      placeholder="Pick a field"
    >
      <template v-slot:option="option">
        <span class="opt-label" :title="option.hint">{{ option.label }}</span>
      </template>
      <template v-slot:selected-option="option">
        <span class="opt-label">{{ option.label }}</span>
      </template>
      <template v-slot:no-options> No field to compare on this page. </template>
    </v-select>

    <div class="condition-test">
      <select class="cond-op" v-model="condition.operator">
        <option v-for="op in operators" :key="op.value" :value="op.value">
          {{ op.label }}
        </option>
      </select>

      <!-- Categories are the one side we can name, so they are picked from a
           list instead of typed as an id. Everything else keeps the free
           field it had. -->
      <v-select
        v-if="picksCategory"
        class="cond-value"
        append-to-body
        :calculate-position="dropdownBelowField"
        v-model="value"
        :options="categories"
        label="name"
        :reduce="(c) => String(c.id)"
        :clearable="false"
        placeholder="Pick a category"
      >
        <template v-slot:option="option">
          <span class="opt-label">{{ option.name }}</span>
        </template>
        <template v-slot:no-options> No categories loaded. </template>
      </v-select>
      <input
        v-else
        class="cond-value"
        v-model="condition.value"
        :placeholder="valuePlaceholder"
      />
    </div>
  </div>
  <!-- The rule in words. A row of three controls can still be read wrong; one
       sentence cannot. -->
  <div class="cond-summary">{{ summary }}</div>
</template>

<script>
import {
  OPERATORS,
  operatorLabel,
  describeVariable,
} from "../modules/page-variables";
import { dropdownBelowField } from "../modules/select-position";

export default {
  props: {
    variables: { type: Array, default: () => [] },
    // The shop's categories, flattened to { id, name } — the only value list
    // the editor can offer today.
    categories: { type: Array, default: () => [] },
    modelValue: { type: Object, required: true },
  },
  emits: ["update:modelValue"],
  data() {
    return { operators: OPERATORS };
  },
  methods: { dropdownBelowField },
  computed: {
    condition: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },
    // A rule saved earlier can point at a field this page no longer offers;
    // it still has to be readable and removable, so it is described rather
    // than dropped.
    field() {
      const known = this.variables.find(
        (v) => v.value === this.condition.variable
      );
      return known ?? describeVariable(this.condition.variable);
    },
    // v-select would not show a value that is missing from its options, and a
    // rule pointing at a retired field would then look empty and get saved
    // back as empty.
    fieldOptions() {
      if (
        !this.condition.variable ||
        this.variables.some((v) => v.value === this.condition.variable)
      ) {
        return this.variables;
      }
      return [...this.variables, this.field];
    },
    variable: {
      get() {
        return this.condition.variable;
      },
      set(val) {
        this.condition.variable = val;
        this.condition.value = "";
      },
    },
    value: {
      get() {
        return this.condition.value === null ||
          this.condition.value === undefined
          ? ""
          : String(this.condition.value);
      },
      set(val) {
        this.condition.value = val;
      },
    },
    // A list of ids is still typed by hand: what the site expects there (a
    // comma-separated list, an array) is not documented, so the editor does
    // not invent a format for it.
    picksCategory() {
      return (
        this.field.valueKind === "category" &&
        this.condition.operator !== "in" &&
        this.condition.operator !== "not in"
      );
    },
    valuePlaceholder() {
      if (
        this.condition.operator === "in" ||
        this.condition.operator === "not in"
      ) {
        return "Values, separated by commas";
      }
      if (this.field.valueHint) {
        return this.field.valueHint;
      }
      return this.field.valueKind === "number" ? "A number" : "Value";
    },
    // What the value means, where the editor can look it up.
    valueName() {
      if (this.field.valueKind !== "category") {
        return null;
      }
      const parts = String(this.condition.value ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part !== "");
      if (parts.length === 0) {
        return null;
      }
      const names = parts.map(
        (part) =>
          this.categories.find((c) => String(c.id) === part)?.name ?? part
      );
      return names.join(", ");
    },
    summary() {
      if (!this.condition.variable) {
        return "Pick a field to compare.";
      }
      const value = this.valueName ?? this.condition.value;
      if (value === "" || value === null || value === undefined) {
        return `${this.field.label} ${operatorLabel(
          this.condition.operator
        )} … — fill in the value`;
      }
      return `${this.field.label} ${operatorLabel(
        this.condition.operator
      )} ${value}`;
    },
  },
};
</script>

<style scoped>
/* The panel this sits in is ~250px wide, so the three controls cannot share a
   row without the value wrapping to two lines. The field takes the first row
   and the test the second, which also reads in the order it is spoken. */
.condition-row {
  display: grid;
  gap: 6px;
}
.condition-test {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: 6px;
  align-items: center;
}
.condition-test select,
.condition-test input {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 9px;
  font-size: 12px;
  font-weight: 600;
  background: #fff;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}
/* One line, cut short if the name is long — the sentence underneath spells it
   out in full anyway. */
.cond-field :deep(.vs__selected-options),
.cond-value :deep(.vs__selected-options) {
  flex-wrap: nowrap;
  overflow: hidden;
}
.cond-field :deep(.vs__selected),
.cond-value :deep(.vs__selected) {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 600;
}
.cond-summary {
  font-size: 11px;
  font-weight: 700;
  color: #768692;
  margin-top: 5px;
  line-height: 1.4;
}
.opt-label {
  font-size: 12px;
  font-weight: 700;
}
</style>
